/**
 * THE PICTURE CONTROL - one button that opens the file picker, and everything the author is told
 * while the file travels: the progress, the check, the result or the refusal.
 *
 * ★ NAMED FOR WHAT IT SETS. The button's name is the caller's - "Choose a cover image", "Add an
 * image" - because a screen reader user hears the name and nothing around it. The hidden file
 * input Mantine's `FileButton` renders carries the same name, so the control is named twice over
 * rather than once and a blank.
 *
 * ★ THE PROGRESS IS A NAMED `progressbar` AND THE PHASE IS A LIVE REGION, and the two are kept
 * apart on purpose. The bar carries the percentage (Mantine puts `aria-label` and the value on the
 * element with the role); the `status` line carries only the phase, so an assistive technology
 * announces "Uploading", "Checking" and the result once each rather than every percent. A refusal
 * is `role="alert"`, the way every refusal on this screen is.
 *
 * ★ THE BUTTON STAYS LIVE WHILE A FILE IS IN FLIGHT. Choosing another file replaces the first
 * (./use-article-image-upload.ts aborts it), which is the only way out of a wrong choice on a slow
 * connection; a spinner that locked the button would take that away.
 *
 * ★ READ-ONLY IS DISABLED, NOT ABSENT - the editor's one pattern for an article nobody may write
 * (G19): every control stays on screen, disabled, under the page's own "not open for editing"
 * alert (../EditorRoute.tsx). An upload already in flight when the article stops being writable is
 * abandoned, because its answer could never be saved into the draft.
 */
import { Button, FileButton, Group, Progress, Stack, Text } from '@mantine/core';
import { useEffect, useRef } from 'react';
import { useTranslation } from '../../../i18n';
import type { ArticlesT } from '../articles-i18n';
import { describeImageUploadFailure, useArticleImageUpload } from './use-article-image-upload';
import type { ArticleImageRole, ArticleImageUploadState } from './use-article-image-upload';
import type { StoredImage } from '../../../features/cabinet/api/editor-api';

/**
 * JPEG, PNG and WebP, listed rather than `image/*`: the platform stores those three and "SVG is
 * rejected at intake" (`StoredImage.contentType`, authors-api.yaml:4142). The claim decides by
 * magic bytes, so this filter is a courtesy to the person picking, never the check.
 */
export const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';

export interface ImageUploadButtonProps {
  readonly articleId: string;
  readonly role: ArticleImageRole;
  /** The button's accessible name: what choosing a file sets. */
  readonly label: string;
  /** Said in the status line once the image is stored. */
  readonly doneMessage: string;
  readonly disabled: boolean;
  /** The stored image, once the claim has answered. */
  readonly onClaimed: (image: StoredImage) => void;
}

function statusFor(
  state: ArticleImageUploadState,
  role: ArticleImageRole,
  doneMessage: string,
  t: ArticlesT,
): string {
  switch (state.phase) {
    case 'uploading':
      return role === 'cover'
        ? t('editor.upload.uploadingCover')
        : t('editor.upload.uploadingImage');
    case 'claiming':
      return role === 'cover' ? t('editor.upload.checkingCover') : t('editor.upload.checkingImage');
    case 'done':
      return doneMessage;
    default:
      return '';
  }
}

export function ImageUploadButton({
  articleId,
  role,
  label,
  doneMessage,
  disabled,
  onClaimed,
}: ImageUploadButtonProps) {
  const { t } = useTranslation('articles');
  const { state, upload, cancel } = useArticleImageUpload({ articleId, role, onClaimed });
  const resetRef = useRef<() => void>(null);

  useEffect(() => {
    if (disabled) {
      cancel();
    }
  }, [disabled, cancel]);

  const percent = state.phase === 'uploading' ? Math.round(state.progress * 100) : 0;

  return (
    <Stack gap={4} align="flex-start">
      <FileButton
        accept={IMAGE_ACCEPT}
        disabled={disabled}
        inputProps={{ 'aria-label': label, disabled }}
        resetRef={resetRef}
        onChange={(file) => {
          // Cleared at once, so the same file can be chosen again after a refusal.
          resetRef.current?.();
          if (file === null || disabled) {
            return;
          }
          void upload(file);
        }}
      >
        {(props) => (
          <Button {...props} variant="default" size="compact-sm" disabled={disabled}>
            {label}
          </Button>
        )}
      </FileButton>

      {state.phase === 'uploading' ? (
        <Group gap="xs" wrap="nowrap">
          <Progress
            value={percent}
            size="sm"
            w="12rem"
            aria-label={
              role === 'cover' ? t('editor.upload.progressCover') : t('editor.upload.progressImage')
            }
          />
          {/* The bar already says the number to assistive technology; this is for the eye. */}
          <Text size="xs" c="dimmed" aria-hidden="true">
            {percent}%
          </Text>
        </Group>
      ) : null}

      {/* ALWAYS MOUNTED, EVEN EMPTY: a live region that appears together with its first message is
          one a screen reader may never announce. */}
      <Text size="xs" c="dimmed" role="status">
        {statusFor(state, role, doneMessage, t)}
      </Text>

      {state.phase === 'failed' ? (
        <Text size="xs" role="alert" style={{ color: 'oklch(var(--mx-danger))' }}>
          {describeImageUploadFailure(state.error, t)}
        </Text>
      ) : null}
    </Stack>
  );
}

/*
 * ★ `disabled` REACHES THE HIDDEN FILE INPUT TOO (`inputProps`, above). Mantine's `FileButton`
 * disables only the click that opens the picker and leaves its `<input type="file">` enabled, so
 * the owner's live walk of real cabinets (2026-10-01) found a locked, published article with two
 * enabled file inputs under two disabled buttons. Written at the foot so that line 112, cited by
 * number, keeps its own.
 */
